// Transient messages — the app's way of answering something the user just
// did, where an inline error has nowhere to live. Field problems still belong
// under the field; this is for the rest.
//
// Ported from the Triva panel's `ToastMessage`, down to the typed variants,
// the coloured left edge and the slide-out. What differs is where the queue
// lives: Triva keeps it in each page's own state and has every page render a
// container, which means a message cannot outlive the screen that raised it.
// Here one provider owns it above the router, so a confirmation raised as a
// page navigates away is still standing on the page that follows.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from "lucide-react";
import { useIsMobile } from "./useIsMobile";

export type ToastType = "success" | "error" | "info" | "warning";

export interface ToastMessage {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

/** How long a message stands before it starts leaving. */
const DEFAULT_DURATION = 3000;

/** Must match the transition on the card, or it is removed mid-slide. */
const EXIT_MS = 300;

type Raise = (title: string, message?: string, duration?: number) => void;

interface ToastApi {
  success: Raise;
  error: Raise;
  warning: Raise;
  info: Raise;
}

const ToastContext = createContext<ToastApi | null>(null);

/**
 * Raises a transient message: `toast.success("Client added", "…")`. Throws
 * outside a `ToastProvider` rather than quietly doing nothing — a message that
 * never appears is a bug that would otherwise only surface in front of a user.
 */
export const useToast = (): ToastApi => {
  const toast = useContext(ToastContext);
  if (!toast) throw new Error("useToast must be called inside a <ToastProvider>");
  return toast;
};

/**
 * Left edge, icon and its halo, per variant. The colour is scoped to the left
 * side rather than set on all four: the other three carry the hairline below,
 * and a whole-border colour would fight it depending on which utility Tailwind
 * happened to emit last.
 *
 * `fill` is the phone's own: there the card has no accent edge to carry the
 * variant — a 4px rule down the side of a full-width capsule reads as a torn
 * strip rather than as an accent — so the colour is a wash over the whole card
 * instead, with the icon and its halo doing the rest. It is a tint of the same
 * hue every time, so the words over it stay the app's ink rather than turning
 * white on a colour, which is what keeps a message legible at a glance.
 */
const VARIANTS: Record<
  ToastType,
  {
    border: string;
    icon: string;
    iconBg: string;
    fill: string;
    Icon: typeof Info;
  }
> = {
  // Success wears the brand teal rather than a green: it is the app's colour
  // for "this worked", and the panel has no second positive shade.
  success: {
    border: "border-l-brand",
    icon: "text-brand",
    iconBg: "bg-brand-soft",
    fill: "bg-[#F2FCFD] border-[#BDEDF1]",
    Icon: CheckCircle,
  },
  error: {
    border: "border-l-[#FF3B30]",
    icon: "text-[#FF3B30]",
    iconBg: "bg-[#FFF5F5]",
    fill: "bg-[#FFF6F5] border-[#FFD4D1]",
    Icon: AlertCircle,
  },
  warning: {
    border: "border-l-[#FF9500]",
    icon: "text-[#FF9500]",
    iconBg: "bg-[#FFF4E5]",
    fill: "bg-[#FFF9F0] border-[#FFE0B8]",
    Icon: AlertTriangle,
  },
  info: {
    border: "border-l-[#007AFF]",
    icon: "text-[#007AFF]",
    iconBg: "bg-[#E5F2FF]",
    fill: "bg-[#F4F9FF] border-[#CBE2FF]",
    Icon: Info,
  },
};

const ToastCard: React.FC<{
  toast: ToastMessage;
  onClose: (id: string) => void;
  /** Whether this is the phone's card — see the two branches below. */
  isMobile: boolean;
}> = ({ toast, onClose, isMobile }) => {
  const [exiting, setExiting] = useState(false);
  const exitTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  /**
   * Where the card was first touched, for the swipe that throws it away.
   *
   * `null` between gestures. Only the upward half of a drag counts: a message
   * that came down off the top edge goes back the way it came, and a downward
   * pull would be fighting the pull-to-refresh the shell puts on every phone
   * screen — see `PullFrame`.
   */
  const touchStart = useRef<number | null>(null);

  const variant = VARIANTS[toast.type];

  // Leaving is two steps: the card slides out, and only then is it dropped
  // from the list — removing it on the spot would cut the animation.
  const close = useCallback(() => {
    setExiting(true);
    exitTimer.current = setTimeout(() => onClose(toast.id), EXIT_MS);
  }, [onClose, toast.id]);

  /** How far up the card has been dragged, in px, while a finger is on it. */
  const [dragged, setDragged] = useState(0);

  useEffect(() => {
    const timer = setTimeout(close, toast.duration ?? DEFAULT_DURATION);
    return () => {
      clearTimeout(timer);
      // A card dismissed by hand and then unmounted must not leave its exit
      // timer running to set state on something that is gone.
      if (exitTimer.current) clearTimeout(exitTimer.current);
    };
  }, [close, toast.duration]);

  const body = (
    <>
      <div className={`flex-shrink-0 rounded-full p-1.5 ${variant.iconBg}`}>
        <variant.Icon aria-hidden="true" className={`h-5 w-5 ${variant.icon}`} />
      </div>

      <div className="min-w-0 flex-1">
        <h4 className="mb-0.5 text-sm font-semibold text-ink">{toast.title}</h4>
        {toast.message && (
          <p className="text-sm leading-snug text-ink-muted">{toast.message}</p>
        )}
      </div>
    </>
  );

  /**
   * The phone's card.
   *
   * A banner that comes down off the top edge and is thrown back up, which is
   * what a phone has done with a transient message since notifications looked
   * like this. Three things differ from the card a pointer gets, and none of
   * them is decoration:
   *
   *  - It is the whole card that is pressed, not a 16px cross in its corner. A
   *    close button that size is a pointer's control; a thumb is told to press
   *    the thing it can see, and the whole banner is it.
   *  - It can be flicked away, because a message standing over the top of the
   *    screen is in the way of the screen, and waiting three seconds for it to
   *    go is the one thing a phone is never asked to do. The drag follows the
   *    finger so the gesture is discoverable by doing it half way.
   *  - The variant is a wash over the card rather than a rule down its side.
   *    At this width an accent edge is a torn strip; a tint is the whole
   *    surface saying what kind of message it is, which is what makes it
   *    readable in the half second it is glanced at.
   *
   * A `button` rather than a div with a handler on it: it is pressed to
   * dismiss, so it is a control, and the keyboard and a screen reader are owed
   * the same way out that the thumb has.
   */
  if (isMobile) {
    return (
      <button
        type="button"
        onClick={close}
        aria-label={`Dismiss message: ${toast.title}`}
        onTouchStart={(event) => {
          touchStart.current = event.touches[0].clientY;
        }}
        onTouchMove={(event) => {
          if (touchStart.current === null) return;
          // Upward only, and never past the card's own height — a banner that
          // can be dragged a screen's length reads as detached from the edge
          // it came from.
          const up = touchStart.current - event.touches[0].clientY;
          setDragged(Math.max(0, Math.min(up, 120)));
        }}
        onTouchEnd={() => {
          touchStart.current = null;
          // A flick is enough: a third of the way is a decision, and making
          // somebody drag the whole card off the screen turns a dismissal into
          // a chore.
          if (dragged > 36) close();
          else setDragged(0);
        }}
        style={
          dragged > 0
            ? { transform: `translateY(-${dragged}px)`, transition: "none" }
            : undefined
        }
        // Full width of the gutter, a 16px corner, and the variant's wash
        // behind a matching edge. The shadow is lighter than the pointer
        // card's: this one is not floating over a white page but hanging off
        // the top of the screen, and it has a tint of its own to sit on.
        className={`flex w-full items-start gap-3 rounded-2xl border p-3.5 text-left shadow-[0_4px_16px_-4px_rgba(16,24,40,0.18),0_12px_32px_-12px_rgba(16,24,40,0.24)] transition-all duration-300 ${
          variant.fill
        } ${
          exiting
            ? "-translate-y-[130%] opacity-0"
            : "translate-y-0 opacity-100 animate-toast-drop"
        }`}
      >
        {body}
      </button>
    );
  }

  return (
    <div
      // A full-weight hairline closes the top, right and bottom, so the card
      // reads as a sheet rather than three soft edges hanging off one coloured
      // one. The app's `line` at 1px rather than a half-pixel custom grey: at
      // 0.5px the browser rounds it away on ordinary displays, which left the
      // card looking like an accent bar with text floating beside it. The left
      // is re-stated after it, which is what lets the accent win: a per-side
      // utility overrides the all-sides one it follows.
      //
      // The shadow is deliberately heavier than anything else in the panel.
      // Every screen this lands on is white, so a card of the same white has
      // only its shadow to sit on: three layers — a tight contact edge, a
      // mid lift, and a wide soft cast — because one blur dark enough to be
      // seen at this size reads as a grey smudge instead of depth.
      className={`w-full rounded-lg border border-line border-l-4 bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.14),0_6px_14px_-3px_rgba(16,24,40,0.22),0_22px_48px_-12px_rgba(16,24,40,0.34)] transition-all duration-300 ${
        variant.border
      } ${
        exiting
          ? "translate-x-full opacity-0"
          : "translate-x-0 opacity-100 animate-toast-in"
      }`}
    >
      <div className="flex items-start gap-3">
        {body}

        <button
          type="button"
          onClick={close}
          aria-label="Dismiss message"
          className="flex-shrink-0 text-ink-subtle transition-colors hover:text-ink"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const nextId = useRef(0);

  /**
   * Which of the two shapes the stack takes.
   *
   * Asked once here rather than inside each card, so a message cannot land as
   * one shape and its neighbour as the other, and so turning a tablet on its
   * side moves the whole stack at once. A stylesheet cannot make this call: the
   * two cards are different elements with different controls in them, not one
   * element at two widths.
   */
  const isMobile = useIsMobile();

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const raise = useCallback(
    (type: ToastType, title: string, message?: string, duration?: number) => {
      // The id is taken outside the updater: the updater has to stay pure, and
      // React may run it more than once for a single call.
      const id = `toast-${nextId.current++}`;

      // Saying the same thing twice stacks nothing — the standing copy is
      // replaced, which is what restarts its clock on a repeated click.
      setToasts((current) => [
        ...current.filter(
          (toast) => toast.title !== title || toast.message !== message
        ),
        { id, type, title, message, duration },
      ]);
    },
    []
  );

  // Rebuilt only when `raise` is, which is never — so a consumer reading the
  // api out of context is not re-rendered by every message that goes past.
  const api = useRef<ToastApi>({
    success: (title, message, duration) =>
      raise("success", title, message, duration),
    error: (title, message, duration) => raise("error", title, message, duration),
    warning: (title, message, duration) =>
      raise("warning", title, message, duration),
    info: (title, message, duration) => raise("info", title, message, duration),
  }).current;

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        // The live region is always mounted, empty or not: one announced late
        // is one a screen reader never reads. `pointer-events-none` on the
        // column keeps the space around the cards clickable, and the width is
        // capped against the viewport so a message never runs off a phone.
        //
        // Nothing clips this column. It is the exact width of the cards, so an
        // `overflow-hidden` here cuts every shadow off flush at the card's own
        // edge — the depth is drawn and then trimmed away, which is why a white
        // card on a white page looked flat. A card sliding out passes beyond
        // the viewport instead, and a fixed box does not extend the document's
        // scrollable area, so it costs no stray horizontal scrollbar.
        <div
          role="status"
          aria-live="polite"
          // Off the top of the viewport, which on a notched phone is behind the
          // status bar and the camera — the shell is drawn edge to edge (see
          // `viewport-fit=cover` in `index.html`), so the stack takes the inset
          // itself. 0px on everything else.
          //
          // A column in the top-right corner for a pointer. On a phone there is
          // no corner to speak of: the stack spans the gutters and hangs off
          // the top edge, centred, which is where a handset has put a passing
          // message for fifteen years. The gap closes to 8px with it — banners
          // stacked at arm's length from each other read as several
          // notifications rather than as one queue.
          className={`pointer-events-none fixed top-[calc(env(safe-area-inset-top)_+_1rem)] z-[9999] flex flex-col ${
            isMobile
              ? "inset-x-3 items-stretch gap-2"
              : "right-4 w-[min(calc(100vw-2rem),24rem)] items-end gap-3"
          }`}
        >
          {toasts.map((toast) => (
            <div key={toast.id} className="pointer-events-auto w-full">
              <ToastCard toast={toast} onClose={dismiss} isMobile={isMobile} />
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
};

export default ToastProvider;
